<template>
  <div class="user-home-page">
    <div v-if="loadingRef">
      <LoadingIndicator/>
    </div>
    <div v-else-if="showUploadActionRef" class="page-content container-lg">
      <div class="title-area container-lg">
        <h1>
          <img src="@/assets/leaf-with-shadow.png"/>
          Hi, {{ selfUserRef?.givenName}}!
        </h1>
        <div class="subtitle">
          <div>Welcome to Tree Crossing!</div>
          <div>You're signed in and ready to start.</div>
        </div>
      </div>
      <div class="start-actions">
        <router-link :to="syncChangesEnabledRef ? '/trees/new' : '/syncapp'" class="btn btn-choice">
          <img src="@/assets/icons/rocket_500.png"/>
          <div>Upload my tree</div>
        </router-link>
        <div>
          <router-link class="btn btn-link" to="/start/family">
            I want to create a family tree
          </router-link>
        </div>
        <div>
          <router-link class="btn btn-link" to="/start/family">
            I want someone to share their tree with me
          </router-link>
        </div>
      </div>
    </div>
    <div v-else class="page-content container-lg">
      <div class="title-area">
        <h2>
          Start exploring your family
        </h2>
      </div>
      <ScopeLoadProgress v-if="scopeLoading"/>
      <template v-else>
      <div class="alerts">
        <div v-if="recentJoinedFamilyGroupRef" class="alert alert-success alert-dismissible">
          <div>            
            Welcome to {{ recentJoinedFamilyGroupRef.name }}!
          </div>
          <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close" @click="dismissJoinedFamilyGroupAlert"></button>
        </div>
      </div>
      <div>
        <div class="activity-row">
          <div class="explore-family">
            <router-link class="btn btn-choice" :class="{ disabled: !exploreStore.enableExploreRef }" :to="exploreFamilyUrlRef ?? ''">
              <img src="@/assets/icons/leafy_tree_180.png"/>
            </router-link>
            <div>Explore my family</div>
          </div>
          <div class="explore-names">
            <router-link to="/names">
              <NameCloud :width="200" :height="120"/>
            </router-link>
            <div>Explore names</div>
          </div>
          <div class="explore-places">
            <router-link to="/places">
              <CountryCloud/>
            </router-link>
            <div>Explore places</div>
          </div>
        </div>
        <div class="activity-row">
          <div class="games">
            <router-link to="/games">
              <img src="@/assets/icons/pawn_150.png"/>
            </router-link>
            <div>Games</div>
          </div>
          <div class="news">
            <router-link to="/news">
              <img src="@/assets/icons/sparkle_60.png"/>
            </router-link>
            <div>What's new</div>
          </div>
          <div class="explore-time">
            <router-link to="/time">
              <CalendarIcon/>
            </router-link>
            <div>Dates</div>
          </div>
          <div class="highlights">
            <router-link to="/highlights">
              <img src="@/assets/icons/highlights_200.png"/>
            </router-link>
            <div>Highlights</div>
          </div>
        </div>
      </div>
      <div class="other-actions">
        <h4>
          Things to do
        </h4>
        <ul class="list-unstyled">
          <li>
            <router-link class="link-dark" to="/trees">
              Manage my trees
            </router-link>
          </li>
          <li>
            <router-link class="link-dark" to="/profile">
              Update my profile
            </router-link>
          </li>
          <li>
            <a href="#" class="link-dark disabled">
              Create child accounts
            </a>
          </li>
          <li>
            <router-link class="link-dark" to="/groups">
              Invite family
            </router-link>
          </li>
        </ul>
      </div>
      </template>
    </div>
    <UserNameConfirmModal ref="userNameConfirmModalRef"></UserNameConfirmModal>
  </div>
</template>

<style lang="scss" scoped>
.user-home-page {
  flex-grow: 1;
  background-color: var(--page-bg-alt);
}

.page-content {
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (min-width: 1200px) {
    padding: 0 2rem;
    display: grid;
    grid-template-columns: 220px 1fr 220px;
    align-content: start;
    align-items: start;

    & > * {
      grid-column: 2;
    }
  }
}

.title-area {
  padding: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #333;

  h1 {
    position: relative;
    font-size: 2rem;
    font-weight: 200;
  }

  img {
    display: none;
  }

  .subtitle {
    font-size: 1.2rem;
    text-align: center;
  }

  @media (min-width: 768px) {
    padding: 2rem;

    h1 {
      font-size: 3rem;
    }

    h2 {
      font-size: 2.2rem;
    }

    img {
      position: absolute;
      display: block;
      top: -8px;
      left: -88px;
      height: 80px;
    }
  }
}

.alerts {
  min-width: 60%;

  @media (min-width: 1200px) {
    justify-self: center;
    min-width: 90%;
  }
}

.start-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;

  .btn-choice {
    border-radius: 12px;
    padding: 1.5rem 1rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    font-size: 1.2rem;

    img {
      width: 50px;
      filter: brightness(0) saturate(100%) invert(40%) sepia(89%) saturate(401%) hue-rotate(99deg) brightness(97%) contrast(93%);
    }
  }

  .btn-link {
    font-size: 0.875rem;
  }

  @media (min-width: 1200px) {
    grid-column: 2;
  } 
}

.activity-row {
  margin-bottom: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3rem;

  & > * {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
  }

  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: center;
    align-items: flex-start;
    gap: 3rem;

    .explore-names {      
      margin-top: 50px;
      order: 0;
    }
    .explore-family {
      order: 1;
    }
    .explore-places {
      order: 2;
      margin-top: 20px;
    }
    .games {
      margin-top: 80px;
    }
    .news {
      margin-top: 30px;
    }
    .highlights {
      margin-top: 80px;
    }
  }

  .explore-family {
    .btn-choice {
      border-radius: 12px;

      img {
        width: 120px;
      }
    }
  }

  .games a img {
    width: 60px;
  }

  .explore-time {
    width: 80px;
  }

  .highlights a img {
    width: 100px;
  }
}

.other-actions {
  margin-top: 2rem;
  max-width: 250px;
  display: flex;
  flex-direction: column;
  justify-content: center;

  h4 {
    border-bottom: 1px solid #ccc;
    padding-bottom: 0.25rem;
    text-align: center;
    color: #888;
  }

  ul {
    padding: 0 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  @media (min-width: 1200px) {
    margin-top: 3rem;
    grid-row: 1 / span 3;
    grid-column: 3;
  } 
}

.btn-choice.disabled {
  opacity: 1;

  img {
    filter: grayscale(1);
    opacity: 20%;
  }
}

</style>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { usePageTitle } from '@/util/AppUtil'
import { useUserStore } from "@/gp/UserStore"
import { useDataGroupStore } from "@/gp/DataGroupStore"
import { useFamilyGroupMemberStore } from '@/gp/FamilyGroupMemberStore'
import { useFamilyGroupStore } from '@/gp/FamilyGroupStore'
import { User } from '@/gp/GroupAdminModel'
import { useTokenManager } from '@/auth/TokenManager'
import { LoadMode } from '@/util/AsyncData'
import { useExploreStore } from './ExploreStore'
import { useUploadAction } from '@/site/UploadAction'
import { DateTime } from 'luxon'
import { ViewPerson } from '@/rd/ResearchDataModel'
import { useFeatureStore, SyncChangesFeature } from '@/rd/FeatureStore'
import LoadingIndicator from '@/util/LoadingIndicator.vue'
import UserNameConfirmModal from "@/manage/users/UserNameConfirmModal.vue"
import ScopeLoadProgress from './ScopeLoadProgress.vue'
import { useViewScopeStore } from './ViewScopeStore'
import NameCloud from './names/NameCloud.vue'
import CountryCloud from './places/CountryCloud.vue'
import CalendarIcon from './time/CalendarIcon.vue'

const selfUserRef = ref<User>()

const userStore = useUserStore()
const dataGroupStore = useDataGroupStore()
const exploreStore = useExploreStore()
const familyGroupMemberStore = useFamilyGroupMemberStore()
const familyGroupStore = useFamilyGroupStore()
const tokenManager = useTokenManager()

const { showUploadActionRef } = useUploadAction()
const featureStore = useFeatureStore()
const syncChangesEnabledRef = computed(() => featureStore.isFeatureEnabled(SyncChangesFeature))

const viewScopeStore = useViewScopeStore()
const scopeLoading = computed(() => !viewScopeStore.getAsyncScopeInfo(exploreStore.startPersonId, LoadMode.EnsureLoaded)?.isLoaded)

const loadingRef = computed(() => showUploadActionRef.value === undefined)
const selfProfileRef = computed(() => dataGroupStore.getAsyncGroup(selfUserRef.value?.profileDataGroupId, LoadMode.EnsureLoaded)?.data)
const recentJoinedFamilyGroupRef = computed(() => {
  const allMem = familyGroupMemberStore.getMembersForUsers(undefined, LoadMode.EnsureLoaded)
  const recentMem = allMem.filter(m => DateTime.utc().diff(m.createdDate!, 'days').days < 1)
    .filter(m => !m.isOwner)
    .sort((a, b) => b.createdDate!.valueOf() - a.createdDate!.valueOf())
    .at(0)
  if (recentMem && !exploreStore.dismissedJoinedGroupIds.includes(recentMem.id!)) {
    return familyGroupStore.getGroupList([recentMem.familyGroupId!]).at(0)
  }
  return undefined
})
const exploreFamilyUrlRef = computed(() => {
  if (selfProfileRef.value) {
    return ViewPerson.getPageUrl(selfProfileRef.value.startItemId!)
  }
  return undefined
})

usePageTitle("Home")

const userNameConfirmModalRef = ref<InstanceType<typeof UserNameConfirmModal>>()

onMounted(async () => {
  selfUserRef.value = await userStore.getAsyncSelf()?.whenLoadCompleted
  if (selfUserRef.value && !tokenManager.current.isSupervised && !selfUserRef.value.metadata?.nameConfirmed) {
    userNameConfirmModalRef.value?.open()
  }
})

function dismissJoinedFamilyGroupAlert() {
  exploreStore.dismissedJoinedGroupIds.push(recentJoinedFamilyGroupRef.value!.id!)
}
</script>
